if (!customElements.get('pdp-upsell-popup')) {
  class PdpUpsellPopup extends HTMLElement {
    constructor() {
      super();

      this.config = this.parseConfig();
      this.modal = this.querySelector('[data-upsell-modal]');
      this.card = this.querySelector('[data-upsell-card]');
      this.acceptButton = this.querySelector('[data-upsell-accept]');
      this.ctaLabel = this.querySelector('[data-upsell-cta-label]');
      this.ctaPrice = this.querySelector('[data-upsell-cta-price]');
      this.skipButton = this.querySelector('[data-upsell-skip]');
      this.closeButton = this.querySelector('[data-upsell-close]');

      this.titleLabel = this.querySelector('[data-upsell-title-label]');
      this.compareEl = this.querySelector('[data-upsell-compare]');
      this.titlePrice = this.querySelector('[data-upsell-title-price]');

      this.optionsContainer = this.querySelector('[data-upsell-options]');
      this.swatchLabel = this.querySelector('[data-upsell-swatch-label]');
      this.selectedFlavor = this.querySelector('[data-upsell-selected-flavor]');
      this.swatches = this.querySelector('[data-upsell-swatches]');
      this.variantName = this.querySelector('[data-upsell-variant-name]');
      this.description = this.querySelector('[data-upsell-description]');
      this.image = this.querySelector('[data-upsell-image]');
      this.imageLink = this.querySelector('[data-upsell-product-url]');
      this.imageWrapper = this.querySelector('[data-upsell-product-url]');

      this.isReplacing = false;
      this.activeOffer = null;
      this.previouslyFocused = null;
      this.previewParams = this.getPreviewParams();
      this.variantHandleOrder = this.getVariantHandleOrder();

      this.handlePdpAdded = this.handlePdpAdded.bind(this);
      this.handleAccept = this.handleAccept.bind(this);
      this.handleBackdropClick = this.handleBackdropClick.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleClose = () => this.closeModal({ openCart: true });
      this.handleSectionSelect = this.handleSectionSelect.bind(this);
      this.handleSectionDeselect = this.handleSectionDeselect.bind(this);
    }

    connectedCallback() {
      if (!this.config) {
        window.console.warn('[pdp-upsell-popup] Disabled: config failed to parse.');
        return;
      }

      if (!this.config.rules?.length) {
        window.console.warn('[pdp-upsell-popup] Disabled: no rule blocks with trigger + upsell product configured.');
        return;
      }

      document.addEventListener('arrae:pdp:added', this.handlePdpAdded);
      this.acceptButton?.addEventListener('click', this.handleAccept);
      this.skipButton?.addEventListener('click', this.handleClose);
      this.closeButton?.addEventListener('click', this.handleClose);
      this.modal?.addEventListener('click', this.handleBackdropClick);

      this.openPreviewOfferIfRequested();
      this.bindThemeEditorEvents();
    }

    disconnectedCallback() {
      document.removeEventListener('arrae:pdp:added', this.handlePdpAdded);
      document.removeEventListener('keydown', this.handleKeydown);
      document.removeEventListener('shopify:section:select', this.handleSectionSelect);
      document.removeEventListener('shopify:section:deselect', this.handleSectionDeselect);
    }

    parseConfig() {
      const configElement = this.querySelector('[data-upsell-config]');

      if (!configElement) return null;

      try {
        return JSON.parse(configElement.textContent);
      } catch (error) {
        window.console.error(
          '[pdp-upsell-popup] Invalid config JSON — popup disabled. Check the rendered script tag for Liquid errors.',
          error,
          configElement.textContent
        );
        return null;
      }
    }

    getPreviewParams() {
      const params = new URLSearchParams(window.location.search);
      const enabledTokens = new Set(['1', 'true', 'yes']);
      const enabled = enabledTokens.has(String(params.get('upsell_preview') || '').toLowerCase());
      const triggerProductId = Number(params.get('upsell_trigger') || 0);

      return {
        enabled,
        triggerProductId: Number.isFinite(triggerProductId) && triggerProductId > 0 ? triggerProductId : null,
      };
    }

    openPreviewOfferIfRequested() {
      if (!this.previewParams.enabled || !this.config?.rules?.length) return;

      const rule = this.previewParams.triggerProductId
        ? this.config.rules.find(
          (item) => Number(item.triggerProductId) === Number(this.previewParams.triggerProductId)
        )
        : this.config.rules[0];

      const primaryUpsellProduct = this.getPrimaryUpsellProduct(rule);
      if (!primaryUpsellProduct?.variants?.length) {
        window.console.warn('[pdp-upsell-popup] Preview mode: no valid upsell rule found to render.');
        return;
      }

      this.activeOffer = this.buildPreviewOfferFromRule(rule);
      if (!this.activeOffer) return;

      this.renderOffer();
      this.openModal();
    }

    bindThemeEditorEvents() {
      if (!window.Shopify?.designMode) return;

      document.addEventListener('shopify:section:select', this.handleSectionSelect);
      document.addEventListener('shopify:section:deselect', this.handleSectionDeselect);
    }

    handleSectionSelect(event) {
      const selectedSectionId = String(event?.detail?.sectionId || '');
      if (!selectedSectionId || selectedSectionId !== String(this.config?.sectionId || '')) return;
      if (!this.config?.rules?.length) return;

      const firstValidRule = this.config.rules.find(
        (rule) => this.getPrimaryUpsellProduct(rule)?.variants?.length
      );
      if (!firstValidRule) return;

      this.activeOffer = this.buildPreviewOfferFromRule(firstValidRule);
      if (!this.activeOffer) return;

      this.renderOffer();
      this.openModal();
    }

    handleSectionDeselect(event) {
      const selectedSectionId = String(event?.detail?.sectionId || '');
      if (!selectedSectionId || selectedSectionId !== String(this.config?.sectionId || '')) return;
      if (!this.activeOffer?.isPreview) return;

      this.closeModal({ openCart: false });
      this.activeOffer = null;
    }

    getRuleUpsellProducts(rule) {
      const rawProducts = Array.isArray(rule?.upsellProducts)
        ? rule.upsellProducts
        : rule?.upsellProduct
          ? [rule.upsellProduct]
          : [];

      const uniqueProducts = new Map();
      rawProducts.forEach((product) => {
        if (!product?.id || !product?.variants?.length) return;
        uniqueProducts.set(Number(product.id), product);
      });

      return Array.from(uniqueProducts.values());
    }

    getVariantHandleOrder() {
      const items = this.querySelectorAll('.pdp-upsell-popup__variant-item[data-handle]');
      const order = new Map();

      items.forEach((item, index) => {
        const handle = this.normalizeFlavorLabel(item.dataset.handle);
        if (!handle || order.has(handle)) return;
        order.set(handle, index);
      });

      return order;
    }

    getPrimaryUpsellProduct(rule) {
      const products = this.getRuleUpsellProducts(rule);
      return products[0] || null;
    }

    shouldShowForPurchaseType(rule, isSubscription) {
      const triggerPurchaseType = String(rule?.triggerPurchaseType || 'any');
      if (triggerPurchaseType === 'one_time') return !isSubscription;
      if (triggerPurchaseType === 'subscription') return isSubscription;
      return true;
    }

    isGuardProductAlreadyInCart(rule, payload) {
      const guardedProductIds = [
        Number(rule?.triggerProductId || 0),
        Number(this.getPrimaryUpsellProduct(rule)?.id || 0),
        Number(rule?.bundleCompareProduct?.id || 0),
      ].filter(Boolean);

      if (!guardedProductIds.length) return false;

      const preAddCartProductIds = Array.isArray(payload?.preAddCartProductIds)
        ? payload.preAddCartProductIds.map((id) => Number(id || 0)).filter(Boolean)
        : [];

      if (!preAddCartProductIds.length) return false;

      const cartProductIds = new Set(preAddCartProductIds);
      return guardedProductIds.some((id) => cartProductIds.has(id));
    }

    closeCartDrawerIfOpen() {
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer?.close && typeof cartDrawer.close === 'function') {
        cartDrawer.close();
      }

      const drawerElement = document.getElementById('cart-drawer');
      if (drawerElement?.classList?.contains('active')) {
        drawerElement.classList.remove('active');
      }
    }

    buildSupplementalSelections(bundleProducts, primaryProductId, isSubscription, cadenceToken) {
      const supplementalSelections = [];

      bundleProducts.forEach((product) => {
        if (Number(product?.id) === Number(primaryProductId)) return;

        const purchaseMatchedVariants = this.filterVariantsByPurchaseType(
          product.variants || [],
          isSubscription,
          cadenceToken
        );
        const selectedVariant = this.pickInitialVariant(purchaseMatchedVariants, isSubscription);
        if (!selectedVariant) return;

        supplementalSelections.push({ product, selectedVariant });
      });

      return supplementalSelections;
    }

    buildPreviewOfferFromRule(rule) {
      const bundleProducts = this.getRuleUpsellProducts(rule);
      const primaryUpsellProduct = bundleProducts[0];
      if (!primaryUpsellProduct?.variants?.length) return null;

      const purchaseMatchedVariants = this.filterVariantsByPurchaseType(
        primaryUpsellProduct.variants,
        false,
        null
      );
      const displayOptionIndex = this.pickDisplayOptionIndex(purchaseMatchedVariants);
      const displayVariants = this.buildDisplayVariants(purchaseMatchedVariants, displayOptionIndex, false);
      const initialVariant = this.pickInitialVariant(displayVariants, false);
      if (!initialVariant) return null;

      return {
        rule,
        upsellProduct: primaryUpsellProduct,
        bundleProducts,
        supplementalSelections: this.buildSupplementalSelections(
          bundleProducts,
          primaryUpsellProduct.id,
          false,
          null
        ),
        isSubscription: false,
        isPreview: true,
        cadenceToken: null,
        sourceItem: {
          productId: rule.triggerProductId,
          variantId: initialVariant.id,
          quantity: 1,
        },
        displayOptionIndex,
        displayVariants,
        selectedVariant: initialVariant,
      };
    }

    async handlePdpAdded(event) {
      if (!this.config?.rules?.length) return;

      const payload = event.detail || {};

      if (!payload.productId) {
        window.console.warn('[pdp-upsell-popup] Ignoring arrae:pdp:added — no productId in event detail.', payload);
        return;
      }

      const quantity = Number(payload.quantity ?? 1);
      if (!Number.isFinite(quantity) || quantity !== 1) {
        return;
      }

      const rule = this.config.rules.find(
        (item) => Number(item.triggerProductId) === Number(payload.productId)
      );

      if (!rule) {
        window.console.info(
          '[pdp-upsell-popup] No rule matches product',
          payload.productId,
          '— configured triggers:',
          this.config.rules.map((r) => r.triggerProductId)
        );
        return;
      }

      const bundleProducts = this.getRuleUpsellProducts(rule);
      const primaryUpsellProduct = bundleProducts[0];
      if (!primaryUpsellProduct?.variants?.length) {
        window.console.warn('[pdp-upsell-popup] Rule matched but upsell product has no variants.', rule);
        return;
      }

      const isSubscription = Boolean(payload.sellingPlanId);
      if (!this.shouldShowForPurchaseType(rule, isSubscription)) {
        return;
      }

      if (this.isGuardProductAlreadyInCart(rule, payload)) {
        return;
      }

      // Mark this synchronously so ATC callers and global cart interceptors do not open the drawer first.
      payload.deferCartDrawer = true;
      window.__pdpUpsellDeferCartDrawer = true;
      window.__pdpUpsellPopupPending = false;

      let cadenceToken = null;

      if (isSubscription) {
        const cartItem = await this.resolveAddedCartItem(payload);
        cadenceToken = this.getCadenceTokenFromLineItem(cartItem);
      }

      const purchaseMatchedVariants = this.filterVariantsByPurchaseType(
        primaryUpsellProduct.variants,
        isSubscription,
        cadenceToken
      );
      const displayOptionIndex = this.pickDisplayOptionIndex(purchaseMatchedVariants);
      const displayVariants = this.buildDisplayVariants(
        purchaseMatchedVariants,
        displayOptionIndex,
        isSubscription
      );
      const initialVariant = this.pickInitialVariant(displayVariants, isSubscription);
      if (!initialVariant) {
        payload.deferCartDrawer = false;
        window.__pdpUpsellDeferCartDrawer = false;
        if (typeof window.refreshCartDrawer === 'function') {
          window.refreshCartDrawer({ open: true });
        }
        return;
      }

      this.activeOffer = {
        rule,
        upsellProduct: primaryUpsellProduct,
        bundleCompareProduct: rule?.bundleCompareProduct || null,
        bundleProducts,
        supplementalSelections: this.buildSupplementalSelections(
          bundleProducts,
          primaryUpsellProduct.id,
          isSubscription,
          cadenceToken
        ),
        isSubscription,
        cadenceToken,
        sourceItem: payload,
        displayOptionIndex,
        displayVariants,
        selectedVariant: initialVariant,
      };

      this.closeCartDrawerIfOpen();
      this.renderOffer();
      this.openModal();
    }

    pickInitialVariant(variants, isSubscription) {
      if (!variants?.length) return null;

      if (isSubscription) {
        const variantWithPlans = variants.find(
          (variant) => variant.available && (variant.selling_plan_allocations || []).length > 0
        );
        if (variantWithPlans) return variantWithPlans;
      }

      return variants.find((variant) => variant.available) || variants[0];
    }

    filterVariantsByPurchaseType(variants, isSubscription, cadenceToken) {
      if (!Array.isArray(variants) || !variants.length) return [];

      if (isSubscription) {
        const subscriptionVariants = variants.filter((variant) => {
          const allocations = variant?.selling_plan_allocations || [];
          if (!allocations.length) return false;

          if (!cadenceToken) return true;
          return allocations.some(
            (allocation) =>
              this.getCadenceTokenFromSellingPlan(allocation.selling_plan) === cadenceToken
          );
        });

        return subscriptionVariants.length ? subscriptionVariants : variants;
      }

      const oneTimeVariants = variants.filter(
        (variant) => (variant?.selling_plan_allocations || []).length === 0
      );

      return oneTimeVariants.length ? oneTimeVariants : variants;
    }

    pickDisplayOptionIndex(variants) {
      if (!Array.isArray(variants) || !variants.length) return 0;

      const optionKeys = ['option1', 'option2', 'option3'];
      let bestIndex = 0;
      let bestCount = 0;

      optionKeys.forEach((optionKey, index) => {
        const uniqueValues = new Set(
          variants
            .map((variant) => this.normalizeOptionValue(variant?.[optionKey]))
            .filter(Boolean)
        );

        if (uniqueValues.size > bestCount) {
          bestCount = uniqueValues.size;
          bestIndex = index;
        }
      });

      return bestCount > 1 ? bestIndex : 0;
    }

    buildDisplayVariants(variants, displayOptionIndex, isSubscription) {
      if (!Array.isArray(variants) || !variants.length) return [];

      const optionKey = `option${displayOptionIndex + 1}`;
      const grouped = new Map();

      variants.forEach((variant) => {
        const groupKey = this.normalizeOptionValue(variant?.[optionKey]) || String(variant.id);
        const current = grouped.get(groupKey);

        if (!current) {
          grouped.set(groupKey, variant);
          return;
        }

        // Prefer an available variant; for subscription prefer one with allocations.
        if (!current.available && variant.available) {
          grouped.set(groupKey, variant);
          return;
        }

        if (
          isSubscription &&
          (current.selling_plan_allocations || []).length === 0 &&
          (variant.selling_plan_allocations || []).length > 0
        ) {
          grouped.set(groupKey, variant);
        }
      });

      const orderedVariants = Array.from(grouped.values());
      const hasCustomOrder = this.variantHandleOrder instanceof Map && this.variantHandleOrder.size > 0;

      if (!hasCustomOrder) return orderedVariants;

      return orderedVariants.sort((left, right) => {
        const leftRank = this.getVariantOrderRank(left, displayOptionIndex);
        const rightRank = this.getVariantOrderRank(right, displayOptionIndex);
        if (leftRank !== rightRank) return leftRank - rightRank;
        return 0;
      });
    }

    normalizeOptionValue(value) {
      return String(value || '')
        .trim()
        .toLowerCase();
    }

    normalizeFlavorLabel(value) {
      return this.humanizeLabel(value)
        .toLowerCase()
        .replace(/\s+/g, ' ')
        .trim();
    }

    getDisplayLabelForOption(variant, displayOptionIndex) {
      const resolvedIndex = Number.isInteger(displayOptionIndex) ? displayOptionIndex : 0;
      const optionKey = `option${resolvedIndex + 1}`;
      const optionValue = String(variant?.[optionKey] || '').trim();
      return this.parseVariantDisplayLabel(optionValue);
    }

    getVariantOrderRank(variant, displayOptionIndex) {
      const label = this.getDisplayLabelForOption(variant, displayOptionIndex);
      const normalizedLabel = this.normalizeFlavorLabel(label);
      if (!normalizedLabel) return Number.MAX_SAFE_INTEGER;

      const explicitRank = this.variantHandleOrder.get(normalizedLabel);
      if (Number.isInteger(explicitRank)) return explicitRank;

      return Number.MAX_SAFE_INTEGER - 1;
    }

    getDisplayLabel(variant) {
      const optionIndex = Number(this.activeOffer?.displayOptionIndex || 0);
      return this.getDisplayLabelForOption(variant, optionIndex);
    }

    parseVariantDisplayLabel(rawLabel) {
      let parsedLabel = this.removeCadenceFromVariantLabel(rawLabel);
      const parserToken = String(this.config?.settings?.variantNameParser || '').trim();

      if (parserToken) {
        const tokenPattern = parserToken
          .toLowerCase()
          .replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
          .replace(/\s+/g, '[_\\s-]+');
        const markerRegex = new RegExp(`(?:^|[_\\s-])(${tokenPattern}[\\w\\s|_-]*)$`, 'i');
        const match = parsedLabel.match(markerRegex);
        if (match?.[1]) {
          parsedLabel = match[1];
        }
      }

      return this.cleanVariantLabel(parsedLabel);
    }

    removeCadenceFromVariantLabel(rawLabel) {
      const label = String(rawLabel || '');

      return label
        .replace(
          /(?:^|[_\s-])(one|two|three|four|five|six|seven|eight|nine|ten|\d+)[_\s-]*(day|week|month|year)s?(?:[_\s-]*delivery)?(?=$|[_\s-])/gi,
          ' '
        )
        .replace(/(?:^|[_\s-])(one|1)[_\s-]*time(?:[_\s-]*purchase)?(?=$|[_\s-])/gi, ' ')
        .replace(/(?:^|[_\s-])subscription(?:[_\s-]*only)?(?=$|[_\s-])/gi, ' ');
    }

    cleanVariantLabel(label) {
      return String(label || '')
        .replace(/\s*\|\s*/g, '_')
        .replace(/[_\s-]{2,}/g, '_')
        .replace(/^[_\s-]+|[_\s-]+$/g, '')
        .trim();
    }

    resolveTitleLabel() {
      const { rule } = this.activeOffer || {};
      const resolvedLabel = String(rule?.resolvedTitleLabel || '').trim();
      if (resolvedLabel) return resolvedLabel;

      return String(this.activeOffer?.upsellProduct?.title || '').trim();
    }

    async resolveAddedCartItem(payload) {
      try {
        const response = await fetch('/cart.js');
        const cart = await response.json();

        if (payload.lineItemKey) {
          const byKey = cart.items.find((item) => item.key === payload.lineItemKey);
          if (byKey) return byKey;
        }

        return (
          cart.items.find((item) => Number(item.variant_id) === Number(payload.variantId)) || null
        );
      } catch (error) {
        window.console.warn('Unable to resolve cart item for cadence matching.', error);
        return null;
      }
    }

    getCadenceTokenFromLineItem(item) {
      const sellingPlan = item?.selling_plan_allocation?.selling_plan;
      return this.getCadenceTokenFromSellingPlan(sellingPlan);
    }

    getCadenceTokenFromSellingPlan(sellingPlan) {
      if (!sellingPlan) return null;

      const optionValues = (sellingPlan.options || []).map((option) => option.value).join(' ');
      const source = `${sellingPlan.name || ''} ${optionValues}`.toLowerCase();
      const matched = source.match(/(\d+)\s*(day|week|month|year)/i);

      return matched ? `${matched[1]}-${matched[2]}` : null;
    }

    getSellingPlanIdForVariant(variant) {
      if (!this.activeOffer?.isSubscription) return null;

      const allocations = variant?.selling_plan_allocations || [];
      if (!allocations.length) return null;

      const cadence = this.activeOffer.cadenceToken;
      if (cadence) {
        const match = allocations.find(
          (allocation) =>
            this.getCadenceTokenFromSellingPlan(allocation.selling_plan) === cadence
        );
        if (match) return String(match.selling_plan_id);
      }

      return String(allocations[0].selling_plan_id);
    }

    getPriceForVariant(variant) {
      if (!variant) return 0;

      if (this.activeOffer?.isSubscription) {
        const sellingPlanId = this.getSellingPlanIdForVariant(variant);
        const allocation = (variant.selling_plan_allocations || []).find(
          (item) => String(item.selling_plan_id) === String(sellingPlanId)
        );
        if (allocation?.price != null) return allocation.price;
      }

      return variant.price;
    }

    getComparePriceForVariant(variant) {
      if (!variant) return 0;

      if (this.activeOffer?.isSubscription) {
        const sellingPlanId = this.getSellingPlanIdForVariant(variant);
        const allocation = (variant.selling_plan_allocations || []).find(
          (item) => String(item.selling_plan_id) === String(sellingPlanId)
        );

        if (allocation?.compare_at_price != null && allocation.compare_at_price > 0) {
          return allocation.compare_at_price;
        }

        return variant.price;
      }

      return Number(variant.compare_at_price || 0);
    }

    getSourceItemUnitPrice() {
      const item = this.activeOffer?.sourceItem?.item;
      if (!item) return 0;

      const quantity = Number(item.quantity || this.activeOffer?.sourceItem?.quantity || 1) || 1;
      const totalPrice = Number(item.final_line_price ?? item.line_price ?? 0);
      if (totalPrice > 0) return Math.round(totalPrice / quantity);

      return Number(item.final_price ?? item.price ?? 0);
    }

    getUpsellItemPrice(primaryVariant) {
      return this.getPriceForVariant(primaryVariant);
    }

    getAdditionalItemsPrice() {
      return (this.activeOffer?.supplementalSelections || []).reduce(
        (total, selection) => total + this.getPriceForVariant(selection.selectedVariant),
        0
      );
    }

    getBundleOfferPrice(primaryVariant) {
      return this.getUpsellItemPrice(primaryVariant) + this.getAdditionalItemsPrice();
    }

    getBundleCompareProductPrice() {
      const compareProduct = this.activeOffer?.bundleCompareProduct;
      if (!compareProduct?.variants?.length) return 0;

      const purchaseMatchedVariants = this.filterVariantsByPurchaseType(
        compareProduct.variants,
        false,
        null
      );
      const selectedVariant = this.pickInitialVariant(purchaseMatchedVariants, false);
      return this.getPriceForVariant(selectedVariant);
    }

    getOfferIncrementalPrice(primaryVariant) {
      const triggerPrice = this.getSourceItemUnitPrice();
      const offerPrice = this.getBundleOfferPrice(primaryVariant);
      return Math.max(0, offerPrice - triggerPrice);
    }

    getOriginalBundleValue(primaryVariant) {
      const bundleCompareProductPrice = this.getBundleCompareProductPrice();
      if (bundleCompareProductPrice > 0) {
        return this.getSourceItemUnitPrice() + bundleCompareProductPrice;
      }

      return this.getSourceItemUnitPrice() + this.getBundleOfferPrice(primaryVariant);
    }

    getBundlePricing(primaryVariant) {
      const offerPrice = this.getBundleOfferPrice(primaryVariant);
      const baseCompare = this.getComparePriceForVariant(primaryVariant);
      const supplementalCompare = (this.activeOffer?.supplementalSelections || []).reduce(
        (total, selection) => total + this.getComparePriceForVariant(selection.selectedVariant),
        0
      );

      const totalCompareAt = baseCompare + supplementalCompare;

      return {
        price: offerPrice,
        compareAtPrice: totalCompareAt > offerPrice ? totalCompareAt : 0,
      };
    }

    renderOffer() {
      if (!this.activeOffer) return;

      const { rule, selectedVariant } = this.activeOffer;
      this.activeOffer.defaultDescription = String(rule?.resolvedDescription || '').trim();

      if (this.ctaLabel) this.ctaLabel.textContent = String(rule?.resolvedCtaLabel || '').trim();
      if (this.description) this.description.textContent = this.activeOffer.defaultDescription;
      if (this.acceptButton) this.acceptButton.toggleAttribute('disabled', Boolean(this.activeOffer.isPreview));

      this.renderSwatches();
      this.applyVariantSelection(selectedVariant);
    }

    setText(node, value) {
      if (node) node.textContent = value;
    }

    getFallbackImageSrc(variant, upsellProduct) {
      return String(variant?.featured_image?.src || upsellProduct?.featured_image || '').trim();
    }

    setFallbackUpsellImage({ variant, upsellProduct, displayLabel }) {
      if (!this.imageWrapper) return;

      const imageSrc = this.getFallbackImageSrc(variant, upsellProduct);
      if (!imageSrc) return;

      let fallbackImage = this.imageWrapper.querySelector('[data-upsell-image]');
      if (!fallbackImage) {
        this.imageWrapper.innerHTML = '';
        fallbackImage = document.createElement('img');
        fallbackImage.className = 'pdp-upsell-popup__image';
        fallbackImage.setAttribute('data-upsell-image', '');
        fallbackImage.width = 360;
        fallbackImage.height = 360;
        fallbackImage.loading = 'lazy';
        this.imageWrapper.appendChild(fallbackImage);
      }

      fallbackImage.src = imageSrc;
      fallbackImage.alt = `${upsellProduct?.title || ''}${displayLabel ? ` – ${displayLabel}` : ''}`;
      this.image = fallbackImage;
    }

    setCustomUpsellImage({ customLabel, upsellProduct, displayLabel }) {
      if (!this.imageWrapper) return false;

      const customImage = customLabel?.querySelector('.c-main-variant-image');
      if (!customImage) return false;

      const clonedCustomImage = customImage.cloneNode(true);
      if (!clonedCustomImage.getAttribute('alt')) {
        clonedCustomImage.setAttribute('alt', `${upsellProduct?.title || ''}${displayLabel ? ` – ${displayLabel}` : ''}`);
      }

      this.imageWrapper.innerHTML = '';
      this.imageWrapper.appendChild(clonedCustomImage);
      this.image = clonedCustomImage;
      return true;
    }

    applyVariantSelection(variant) {
      if (!variant) return;
      this.activeOffer.selectedVariant = variant;

      const { upsellProduct } = this.activeOffer;
      const displayLabel = this.getDisplayLabel(variant);
      const bundlePricing = this.getBundlePricing(variant);
      const showCtaPrice = this.config.settings.showCtaPrice !== false;
      const titlePriceValue = this.getOfferIncrementalPrice(variant);
      const titleCompareValue = this.getBundleCompareProductPrice();

      this.setText(this.titleLabel, this.resolveTitleLabel());
      this.setText(this.titlePrice, titlePriceValue ? this.formatMoney(titlePriceValue) : '');
      this.setText(this.ctaPrice, showCtaPrice && titlePriceValue ? this.formatMoney(titlePriceValue) : '');

      if (this.compareEl) {
        const hasCompare =
          Number(titleCompareValue || 0) > 0 && Number(titleCompareValue || 0) > Number(titlePriceValue || 0);
        this.compareEl.textContent = hasCompare ? this.formatMoney(titleCompareValue) : '';
        this.compareEl.hidden = !hasCompare;
      }

      if (this.variantName) {
        this.variantName.textContent = this.humanizeLabel(displayLabel).toUpperCase();
        this.variantName.hidden = !displayLabel;
      }

      if (this.selectedFlavor) {
        const displayLabelHandle = displayLabel?.trim().toLowerCase();

        const customLabel = document.querySelector(
          `.pdp-upsell-popup__variant-item[data-handle="${displayLabelHandle}"]`
        );

        let content = '';

        if (customLabel) {
          content = customLabel.outerHTML;
          this.selectedFlavor.innerHTML = `${content}`;
        } else if (displayLabel) {
          content = this.humanizeLabel(displayLabel);
          this.selectedFlavor.textContent = ` - ${content}`;
        } else {
          this.selectedFlavor.textContent = '';
        }

        this.selectedFlavor.hidden = !content;
      }

      const displayLabelHandle = displayLabel?.trim().toLowerCase();
      const customLabel = displayLabelHandle
        ? document.querySelector(`.pdp-upsell-popup__variant-item[data-handle="${displayLabelHandle}"]`)
        : null;
      const hasCustomImage = this.setCustomUpsellImage({ customLabel, upsellProduct, displayLabel });
      if (!hasCustomImage) {
        this.setFallbackUpsellImage({ variant, upsellProduct, displayLabel });
      }

      if (this.description) {
        const variantDescription = String(variant?.variant_description || '').trim();
        this.description.textContent = variantDescription || this.activeOffer.defaultDescription || '';
      }

      if (this.imageLink) {
        this.imageLink.href = upsellProduct.url ? `${upsellProduct.url}?variant=${variant.id}` : '#';
      }

      this.swatches?.querySelectorAll('[data-variant-id]').forEach((node) => {
        const isActive = Number(node.dataset.variantId) === Number(variant.id);
        node.setAttribute('aria-checked', isActive ? 'true' : 'false');
        node.tabIndex = isActive ? 0 : -1;
      });

    }

    renderSwatches() {
      if (!this.swatches || !this.optionsContainer) return;
      this.swatches.innerHTML = '';

      if (!this.config.settings.showScentSwatches || !this.activeOffer?.displayVariants?.length) {
        this.optionsContainer.hidden = true;
        return;
      }

      const variants = this.activeOffer.displayVariants.filter((variant) =>
        this.getDisplayLabel(variant)
      );
      if (variants.length < 2) {
        this.optionsContainer.hidden = true;
        return;
      }

      const fragment = document.createDocumentFragment();

      variants.forEach((variant) => {
        const swatch = document.createElement('button');
        swatch.type = 'button';
        swatch.className = 'pdp-upsell-popup__swatch';
        swatch.setAttribute('role', 'radio');
        const displayLabel = this.getDisplayLabel(variant);
        swatch.setAttribute('aria-label', displayLabel);
        swatch.setAttribute('aria-checked', 'false');
        swatch.tabIndex = -1;
        swatch.dataset.variantId = variant.id;

        const swatchColor = this.getSwatchColor(displayLabel);
        swatch.style.backgroundImage = '';
        if (swatchColor) {
          swatch.style.background = '';
          swatch.style.setProperty('--swatch-color', swatchColor);
          swatch.classList.remove('pdp-upsell-popup__swatch--empty');
        } else {
          swatch.style.background = '';
          swatch.style.setProperty('--swatch-color', 'transparent');
          swatch.classList.add('pdp-upsell-popup__swatch--empty');
        }

        swatch.addEventListener('click', () => {
          this.applyVariantSelection(variant);
        });

        fragment.appendChild(swatch);
      });

      this.swatches.appendChild(fragment);
      this.optionsContainer.hidden = false;
    }

    getSwatchColor(optionValue) {
      const value = String(optionValue || '').toLowerCase();
      const palette = [
        ['mixed berry', '#b04376'],
        ['green apple', '#c7d84f'],
        ['sour watermelon', '#ef5d76'],
        ['flavor trio', 'linear-gradient(90deg, #ef5d76 0%, #b04376 50%, #c7d84f 100%)'],
        ['santal', '#e0cfa5'],
        ['vetiver', '#9aa57a'],
        ['rose', '#eab0a6'],
        ['lavender', '#cbbcd8'],
        ['mint', '#bfe0ca'],
        ['eucalyptus', '#bfe0ca'],
        ['citrus', '#edd878'],
        ['lemon', '#f1e08a'],
        ['cedar', '#b08e6e'],
        ['oak', '#b08e6e'],
        ['coconut', '#f2e9d8'],
        ['vanilla', '#f4e3c4'],
        ['sea', '#b7cfd8'],
        ['ocean', '#b7cfd8'],
      ];

      const found = palette.find(([keyword]) => value.includes(keyword));
      return found ? found[1] : '';
    }

    humanizeLabel(value) {
      return String(value || '')
        .replace(/[_-]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    }

    formatMoney(cents) {
      const value = Number(cents || 0);
      const format = this.config.settings.moneyFormat;

      if (window.Shopify?.formatMoney && format) {
        let formatted = window.Shopify.formatMoney(value, format);
        // Whole-dollar amounts: drop meaningless .00 / ,00 fraction (keep e.g. .50, .99).
        if (value % 100 === 0) {
          formatted = formatted
            .replace(/\.00(?!\d)/g, '')
            .replace(/,00(?!\d)/g, '');
        }
        return formatted;
      }

      const amount = value / 100;
      const display = value % 100 === 0 ? String(Math.round(amount)) : amount.toFixed(2);
      return `$${display}`;
    }

    async handleAccept() {
      if (!this.activeOffer || this.isReplacing) return;
      if (this.activeOffer.isPreview) {
        this.closeModal({ openCart: false });
        return;
      }

      this.isReplacing = true;
      this.acceptButton.setAttribute('disabled', 'disabled');

      try {
        await this.removeSourceLineItem(this.activeOffer.sourceItem);
        await this.addUpsellItem();
        this.closeModal({ openCart: true });
      } catch (error) {
        window.console.error('Unable to replace cart item with upsell.', error);
      } finally {
        this.isReplacing = false;
        this.acceptButton.removeAttribute('disabled');
      }
    }

    async removeSourceLineItem(sourceItem) {
      if (sourceItem.lineItemKey) {
        const response = await fetch(routes.cart_change_url, {
          ...fetchConfig(),
          body: JSON.stringify({
            id: sourceItem.lineItemKey,
            quantity: 0,
          }),
        });
        if (!response.ok) throw new Error('Unable to remove original line item by key.');
        return;
      }

      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();
      const itemIndex = cart.items.findIndex(
        (item) => Number(item.variant_id) === Number(sourceItem.variantId)
      );

      if (itemIndex === -1) return;

      const response = await fetch(routes.cart_change_url, {
        ...fetchConfig(),
        body: JSON.stringify({
          line: itemIndex + 1,
          quantity: 0,
        }),
      });
      if (!response.ok) throw new Error('Unable to remove original line item by index.');
    }

    async addUpsellItem() {
      const { selectedVariant, supplementalSelections, sourceItem } = this.activeOffer;
      const quantity = Number(sourceItem.quantity || 1);
      const lineItemProperties = {
        _pdp_upsell_upgrade: 'true',
        _pdp_upsell_trigger_variant_id: String(sourceItem.variantId || ''),
        _pdp_upsell_rule_id: String(this.activeOffer?.rule?.blockId || ''),
        _pdp_upsell_purchase_type: this.activeOffer?.isSubscription ? 'subscription' : 'one_time',
      };
      const selectedBundleItems = this.activeOffer?.bundleCompareProduct
        ? [{ selectedVariant }]
        : [{ selectedVariant }, ...(supplementalSelections || [])];
      const bundleItems = selectedBundleItems.map((selection) => {
        const payload = {
          id: Number(selection.selectedVariant.id),
          quantity,
          properties: lineItemProperties,
        };

        const sellingPlanId = this.getSellingPlanIdForVariant(selection.selectedVariant);
        if (sellingPlanId) payload.selling_plan = Number(sellingPlanId);
        return payload;
      });

      // Use `items` so all bundle parts are added atomically in a single request.
      const payload = bundleItems.length === 1 ? bundleItems[0] : { items: bundleItems };

      const response = await fetch(routes.cart_add_url, {
        ...fetchConfig('javascript'),
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error('Unable to add selected upsell item.');
    }

    openModal() {
      if (!this.modal) return;

      window.__pdpUpsellPopupOpen = true;
      this.previouslyFocused = document.activeElement;
      this.modal.setAttribute('open', '');
      this.modal.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('pdp-upsell-popup-open');
      document.body.style.overflow = 'hidden';
      document.addEventListener('keydown', this.handleKeydown);

      window.requestAnimationFrame(() => {
        if (typeof window.trapFocus === 'function') {
          window.trapFocus(this.card, this.acceptButton);
        } else {
          this.acceptButton?.focus();
        }
      });
    }

    openCartAfterPopup() {
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));

      if (typeof window.refreshCartDrawer === 'function') {
        window.refreshCartDrawer({ open: true });
        return;
      }

      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer?.handleAddedItem && this.activeOffer?.sourceItem?.item) {
        cartDrawer.handleAddedItem(this.activeOffer.sourceItem.item);
        return;
      }

      if (cartDrawer?.open && typeof cartDrawer.open === 'function') {
        cartDrawer.open();
      }
    }

    closeModal(options = {}) {
      if (!this.modal) return;
      const shouldOpenCart = options?.openCart === true && !this.activeOffer?.isPreview;

      this.modal.removeAttribute('open');
      this.modal.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('pdp-upsell-popup-open');
      document.body.style.overflow = '';
      document.removeEventListener('keydown', this.handleKeydown);
      window.__pdpUpsellPopupOpen = false;
      window.__pdpUpsellDeferCartDrawer = false;

      if (typeof window.removeTrapFocus === 'function') {
        window.removeTrapFocus(this.previouslyFocused);
      } else {
        this.previouslyFocused?.focus?.();
      }

      if (shouldOpenCart) {
        this.openCartAfterPopup();
      }
    }

    handleBackdropClick(event) {
      if (event.target === this.modal) {
        this.closeModal({ openCart: true });
      }
    }

    handleKeydown(event) {
      if (event.key === 'Escape' || event.code === 'Escape') {
        event.stopPropagation();
        this.closeModal({ openCart: true });
      }
    }
  }

  customElements.define('pdp-upsell-popup', PdpUpsellPopup);
}
